import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import reduce from 'lodash/reduce';

import IvrNode from 'src/models/Ivrs/IvrNode';
import NODE_TYPES from 'src/models/Ivrs/IvrNode';
import { nodesHierarchy, NodesHierarchyType } from 'src/presentation/components/pages/ivr/helpers/configurationHelper';

export interface IvrConfiguration {
  comment: string;
  createdAt?: string;
  createdBy?: string;
  description: string;
  id: string;
  name: string;
  nodes: IvrNode[];
  nodesMap?: { [nodeId: string]: IvrNode };
  publishedAt?: string;
  publishedBy?: string;
  rootNodeId: string;
  version: number;
}

export interface IvrConfigNode {
  type: NODE_TYPES;
  ivrNode: {
    id: string;
    description: string;
    nextId?: string;
    defaultNextId?: string;
    errorNodeId?: string;
    variableMatches?: { nextId: string }[];
  };
}

interface IvrNodeWithCoordinates extends IvrNode {
  height: number;
  id: string;
  name: string;
  width: number;
  x: number;
  y: number;
}

export class IvrConfig {
  private _nodesHierarchy: NodesHierarchyType;
  private _nodesMap: { [nodeId: string]: IvrNodeWithCoordinates };
  private _rootNodeId: string;

  constructor(config: IvrConfiguration) {
    this._rootNodeId = config.rootNodeId;
    this._nodesMap = this.buildMap(config);
    this._nodesHierarchy = nodesHierarchy(this, this.rootNodeId);
  }

  get rootNodeId() {
    return this._rootNodeId;
  }

  get rootNode() {
    return this.findById(this.rootNodeId);
  }

  isValid() {
    return this.rootNodeId && this.findById(this.rootNodeId);
  }

  findById(id: string) {
    return this._nodesMap[id];
  }

  getParentId(id: string) {
    return find(Object.keys(this._nodesHierarchy), nodeId =>
      map(get(this._nodesHierarchy, `[${nodeId}]`), children => children.id).includes(id)
    );
  }

  get nodesMap() {
    return this._nodesMap;
  }

  get nodesHierarchy() {
    return this._nodesHierarchy;
  }

  private buildMap(config) {
    if (!isEmpty(config.nodesMap)) {
      return config.nodesMap;
    }

    return reduce(
      config.nodes,
      (acc: { [nodeId: string]: IvrNode }, node: IvrNode) => {
        acc[node.ivrNode.id] = node;
        return acc;
      },
      {}
    );
  }
}
